// A minimal client-side router on the History API: the app has a handful of
// routes, and nginx serves index.html for every path (web/docker).
import { useSyncExternalStore } from 'react';

export type Route =
  | { name: 'home' }
  | { name: 'site'; siteId: string }
  | { name: 'schedules'; siteId: string }
  | { name: 'telemetry'; siteId: string }
  | { name: 'remote'; siteId: string }
  | { name: 'alerts' }
  | { name: 'apiKeys' }
  | { name: 'notFound' };

const SITE = /^\/sites\/([^/]+)\/?$/;
const SCHEDULES = /^\/sites\/([^/]+)\/schedules\/?$/;
const TELEMETRY = /^\/sites\/([^/]+)\/telemetry\/?$/;
const REMOTE = /^\/sites\/([^/]+)\/remote\/?$/;

export function parseRoute(path: string): Route {
  const site = SITE.exec(path);
  if (site?.[1]) return { name: 'site', siteId: decodeURIComponent(site[1]) };
  const schedules = SCHEDULES.exec(path);
  if (schedules?.[1]) return { name: 'schedules', siteId: decodeURIComponent(schedules[1]) };
  const telemetry = TELEMETRY.exec(path);
  if (telemetry?.[1]) return { name: 'telemetry', siteId: decodeURIComponent(telemetry[1]) };
  const remote = REMOTE.exec(path);
  if (remote?.[1]) return { name: 'remote', siteId: decodeURIComponent(remote[1]) };
  if (path === '/alerts' || path === '/alerts/') return { name: 'alerts' };
  if (path === '/api-keys' || path === '/api-keys/') return { name: 'apiKeys' };
  if (path === '/' || path === '') return { name: 'home' };
  return { name: 'notFound' };
}

export function routePath(r: Route): string {
  switch (r.name) {
    case 'site':
      return `/sites/${encodeURIComponent(r.siteId)}`;
    case 'schedules':
      return `/sites/${encodeURIComponent(r.siteId)}/schedules`;
    case 'telemetry':
      return `/sites/${encodeURIComponent(r.siteId)}/telemetry`;
    case 'remote':
      return `/sites/${encodeURIComponent(r.siteId)}/remote`;
    case 'alerts':
      return '/alerts';
    case 'apiKeys':
      return '/api-keys';
    default:
      return '/';
  }
}

const NAVIGATE = 'illumilink:navigate';

export function navigate(path: string, replace = false) {
  if (path === window.location.pathname) return;
  if (replace) window.history.replaceState({}, '', path);
  else window.history.pushState({}, '', path);
  window.dispatchEvent(new Event(NAVIGATE));
  window.scrollTo(0, 0);
}

function subscribe(onChange: () => void) {
  window.addEventListener('popstate', onChange);
  window.addEventListener(NAVIGATE, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(NAVIGATE, onChange);
  };
}

/** The current path, re-rendering on navigation. */
export function usePath(): string {
  return useSyncExternalStore(subscribe, () => window.location.pathname);
}

export function useRoute(): Route {
  return parseRoute(usePath());
}

/** onClick for links rendered as anchors: navigate in-app on a plain click. */
export function linkHandler(path: string) {
  return (e: React.MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    navigate(path);
  };
}
